import { isAxiosError, type AxiosError } from 'axios';
import { readDevEntraAccessToken } from './dev-entra-token-storage';
import { apiClient } from './client';

export type InvitationTokenErrorCode =
  | 'TOKEN_NOT_FOUND'
  | 'TOKEN_EXPIRED'
  | 'TOKEN_REVOKED';

export interface InvitationTokenExpiredDetails {
  variant: 'contact_cpa' | 'integration_owner';
  owner?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface RevokedIntegrationRow {
  id: string;
  status: 'active' | 'revoked';
}

export interface InvitationTokenRevokedDetails {
  variant: 'invitation_revoked';
  externalCompanyId: string;
  partnerApplicationId: string;
  integrations: RevokedIntegrationRow[];
  activeIntegrationCreator?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export function isInvitationTokenErrorCode(
  code: unknown,
): code is InvitationTokenErrorCode {
  return (
    code === 'TOKEN_NOT_FOUND' ||
    code === 'TOKEN_EXPIRED' ||
    code === 'TOKEN_REVOKED'
  );
}

/** RFC 9457 problem+json uses `detail` and `errorCode`; older shape used `message` and `code`. */
function invitationTokenErrorFromAxiosError(err: unknown): {
  code: InvitationTokenErrorCode;
  message: string;
  expiredDetails?: InvitationTokenExpiredDetails;
  revokedDetails?: InvitationTokenRevokedDetails;
} | null {
  if (typeof err !== 'object' || err === null) return null;
  const ax = err as AxiosError<Record<string, unknown>>;
  if (ax.response?.status !== 401) return null;
  const data = ax.response.data;
  if (data == null || typeof data !== 'object') return null;
  const rawCode = data.code ?? data.errorCode;
  if (typeof rawCode !== 'string' || !isInvitationTokenErrorCode(rawCode)) {
    return null;
  }
  const message =
    (typeof data.detail === 'string' && data.detail) ||
    (typeof data.message === 'string' && data.message) ||
    'Unauthorized';
  let expiredDetails: InvitationTokenExpiredDetails | undefined;
  if (data.expiredDetails != null && typeof data.expiredDetails === 'object') {
    expiredDetails = data.expiredDetails as InvitationTokenExpiredDetails;
  }
  let revokedDetails: InvitationTokenRevokedDetails | undefined;
  if (data.revokedDetails != null && typeof data.revokedDetails === 'object') {
    const rd = data.revokedDetails as Record<string, unknown>;
    if (rd.variant === 'invitation_revoked') {
      const ec = rd.externalCompanyId;
      const pa = rd.partnerApplicationId;
      const integrationsRaw = rd.integrations;
      const integrations: RevokedIntegrationRow[] = Array.isArray(
        integrationsRaw,
      )
        ? integrationsRaw
            .filter(
              (row): row is Record<string, unknown> =>
                row != null && typeof row === 'object',
            )
            .map((row) => {
              const id = row.id;
              const status = row.status;
              if (typeof id !== 'string') {
                return null;
              }
              if (status !== 'active' && status !== 'revoked') {
                return null;
              }
              return { id, status };
            })
            .filter((x): x is RevokedIntegrationRow => x !== null)
        : [];
      const ac = rd.activeIntegrationCreator;
      if (typeof ec === 'string' && typeof pa === 'string') {
        const base: InvitationTokenRevokedDetails = {
          variant: 'invitation_revoked',
          externalCompanyId: ec,
          partnerApplicationId: pa,
          integrations,
        };
        if (ac != null && typeof ac === 'object') {
          const c = ac as Record<string, unknown>;
          const firstName = c.firstName;
          const lastName = c.lastName;
          const email = c.email;
          if (
            typeof firstName === 'string' &&
            typeof lastName === 'string' &&
            typeof email === 'string'
          ) {
            base.activeIntegrationCreator = {
              firstName,
              lastName,
              email,
            };
          }
        }
        revokedDetails = base;
      }
    }
  }
  return { code: rawCode, message, expiredDetails, revokedDetails };
}

function throwIfInvitationTokenUnauthorized(err: unknown): void {
  const parsed = invitationTokenErrorFromAxiosError(err);
  if (!parsed) return;
  const e = new Error(parsed.message) as Error & {
    code: InvitationTokenErrorCode;
    expiredDetails?: InvitationTokenExpiredDetails;
    revokedDetails?: InvitationTokenRevokedDetails;
  };
  e.code = parsed.code;
  if (parsed.expiredDetails != null) {
    e.expiredDetails = parsed.expiredDetails;
  }
  if (parsed.revokedDetails != null) {
    e.revokedDetails = parsed.revokedDetails;
  }
  throw e;
}

/** Mirrors api-gateway invitation / API key flow (snake_case inside for parity with `public_api_integration_id`). */
export interface InvitationFlowDisplayContext {
  business_owner_first_name: string | null;
  business_owner_last_name: string;
  cpa_last_name: string;
  inviter_company_name: string;
  collaborator_first_name: string | null;
  collaborator_last_name: string;
  partner_name: string;
  partner_description: string;
  partner_logo_url: string | null;
}

export interface ValidateInvitationResponse {
  externalCompanyId: string;
  partnerApplicationId: string;
  integrationServiceLabel: string;
  invitationId: string;
  /** Email masked for display (e.g. joh***e@gmail.com). Null if no email. */
  maskedCustomerEmail: string | null;
  display_context: InvitationFlowDisplayContext;
}

export interface InvitationErrorResponse {
  code: InvitationTokenErrorCode;
  message?: string;
  expiredDetails?: InvitationTokenExpiredDetails;
}

export interface GenerateApiKeyResponse {
  /** Primary key `public_api_integrations.id` (path segment `integrationId` on import routes). */
  public_api_integration_id: string;
  /** Plain secret; shown only once at generation. */
  api_key_secret: string;
  display_context: InvitationFlowDisplayContext;
}

export interface SendVerificationCodeResponse {
  success: boolean;
}

export type ValidateVerificationCodeReason =
  | 'expired'
  | 'invalid'
  | 'max_attempts';

export interface ValidateVerificationCodeResponse {
  valid: boolean;
  reason?: ValidateVerificationCodeReason;
}

/** User locale for email content: 'fr' (French) or 'en' (English). */
export type SendVerificationCodeLocale = 'fr' | 'en';

export async function sendVerificationCode(
  token: string,
  locale?: SendVerificationCodeLocale,
): Promise<SendVerificationCodeResponse> {
  const { data } = await apiClient
    .post<SendVerificationCodeResponse>('/invitations/send-code', {
      token,
      ...(locale != null && { locale }),
    })
    .catch((err) => {
      throwIfInvitationTokenUnauthorized(err);
      throw err;
    });
  return data;
}

export async function validateVerificationCode(
  token: string,
  code: string,
): Promise<ValidateVerificationCodeResponse> {
  const { data } = await apiClient.post<ValidateVerificationCodeResponse>(
    '/invitations/validate-code',
    { token, code },
  );
  return data;
}

export async function validateInvitationToken(
  token: string,
): Promise<ValidateInvitationResponse> {
  const { data } = await apiClient
    .post<ValidateInvitationResponse>('/invitations/validate', { token })
    .catch((err) => {
      throwIfInvitationTokenUnauthorized(err);
      throw err;
    });
  return data;
}

export const SESSION_EXPIRED_CODE = 'SESSION_EXPIRED' as const;

export async function generateInvitationApiKey(
  token: string,
): Promise<GenerateApiKeyResponse> {
  const { data } = await apiClient
    .post<GenerateApiKeyResponse>('/api-keys/generate', { token })
    .catch((err: unknown) => {
      throwIfInvitationTokenUnauthorized(err);
      if (
        isAxiosError(err) &&
        err.response?.status === 400 &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'code' in err.response.data &&
        (err.response.data as { code?: string }).code === SESSION_EXPIRED_CODE
      ) {
        const body = err.response.data as { message?: string };
        const e = new Error(body.message ?? 'Session expired') as Error & {
          code: typeof SESSION_EXPIRED_CODE;
        };
        e.code = SESSION_EXPIRED_CODE;
        throw e;
      }
      throw err;
    });
  return data;
}

/**
 * Partner-facing revoke: {@code PUT /api/v1/integrations/:integrationId/revoke}
 * with Entra Bearer (via BFF). Body uses a fixed codified reason.
 */
export async function revokeApiKey(integrationId: string): Promise<void> {
  await apiClient.put(
    `/integrations/${encodeURIComponent(integrationId)}/revoke`,
    { revocationReason: 'OrganizationRequest' },
  );
}

export interface RevokeApiKeyByContextParams {
  externalCompanyId: string;
  partnerApplicationId: string;
  revocationReason: string;
}

export async function revokeApiKeyByContext(
  params: RevokeApiKeyByContextParams,
  options?: { authorizationBearer?: string | null },
): Promise<void> {
  const bearer = options?.authorizationBearer;
  await apiClient.put(
    '/internal/integrations/revoke',
    {
      externalCompanyId: params.externalCompanyId,
      partnerApplicationId: params.partnerApplicationId,
      revocationReason: params.revocationReason,
    },
    bearer != null && bearer !== ''
      ? { headers: { Authorization: `Bearer ${bearer}` } }
      : undefined,
  );
}

/** Dev / Console: Bearer from Entra client-credentials flow (session + localStorage mirror in dev). */
export function getStoredEntraAccessToken(): string | null {
  return readDevEntraAccessToken();
}

export async function regenerateApiKeySecretForCpa(params: {
  externalCompanyId: string;
  partnerApplicationId: string;
  authorizationBearer: string;
  /** Active integration row to scope rotation; must belong to the customer and product. */
  publicApiIntegrationId?: string;
}): Promise<GenerateApiKeyResponse> {
  const { data } = await apiClient.post<GenerateApiKeyResponse>(
    '/internal/integrations/regenerate-api-key',
    {
      externalCompanyId: params.externalCompanyId,
      partnerApplicationId: params.partnerApplicationId,
      ...(params.publicApiIntegrationId != null &&
        params.publicApiIntegrationId !== '' && {
          publicApiIntegrationId: params.publicApiIntegrationId,
        }),
    },
    {
      headers: { Authorization: `Bearer ${params.authorizationBearer}` },
    },
  );
  return data;
}

export interface RevokeApiKeysByIdsParams {
  externalCompanyId: string;
  partnerApplicationId: string;
  publicApiIntegrationIds: string[];
  revocationReason: string;
}

export async function revokeApiKeysByIdsForCpa(
  params: RevokeApiKeysByIdsParams,
  options?: { authorizationBearer?: string | null },
): Promise<{ revokedCount: number; alreadyRevokedCount: number }> {
  const bearer = options?.authorizationBearer;
  const { data } = await apiClient.put<{
    revokedCount: number;
    alreadyRevokedCount: number;
  }>(
    '/internal/integrations/revoke-by-ids',
    {
      externalCompanyId: params.externalCompanyId,
      partnerApplicationId: params.partnerApplicationId,
      publicApiIntegrationIds: params.publicApiIntegrationIds,
      revocationReason: params.revocationReason,
    },
    bearer != null && bearer !== ''
      ? { headers: { Authorization: `Bearer ${bearer}` } }
      : undefined,
  );
  return data;
}
